/**
 * MyCollectionPage — `/library`
 *
 * The user's central library of EVERYTHING they've curated across the
 * app: audios (Pre-Performance, Affirmation Installations, Collective
 * reframes, Voice Gifts), immersions (Vision Board saves), and
 * personalised images.
 *
 * Tabbed, premium gold/navy aesthetic to match Aurum / Collective.
 * Each item has a Share button → generates a "Session Recipe" share card
 * + a short public link.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Headphones, Sparkles, Image as ImageIcon, Play, Pause,
  Share2, Check, Download, Eye, Trash2,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { premiumPageBg, premiumCard, premiumEyebrow, premiumHeading, premiumDivider, ACCENTS } from '../utils/premiumStyle';
import { useUndoToast } from '../components/UndoToast';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem('reclaim_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const TABS = [
  { id: 'all',       label: 'All',        icon: Sparkles },
  { id: 'audio',     label: 'Audios',     icon: Headphones },
  { id: 'immersion', label: 'Immersions', icon: Sparkles },
  { id: 'image',     label: 'Images',     icon: ImageIcon },
];

// Category-first grouping — the "personally curated audio library"
// experience Michael asked for. Each category maps to one-or-more
// backend sources. Ordering here is the visual ordering on the page.
// Empty categories collapse automatically (see grouped() below).
const CATEGORIES = [
  {
    id: 'realities',
    label: 'Your Realities',
    hint: 'Rockstar · Billionaire · Champion — voiced in your own words.',
    accent: '#F472B6',
    sources: ['reality_state'],
  },
  {
    id: 'alignments',
    label: 'Alignments',
    hint: 'Instant Alignment sessions you saved.',
    accent: '#D2AF82',
    sources: ['alignment_save'],
  },
  {
    id: 'states',
    label: 'States',
    hint: 'The desired states you shaped.',
    accent: '#A78BFA',
    sources: ['saved_state'],
  },
  {
    id: 'installations',
    label: 'Installations',
    hint: 'Affirmations installed in your voice.',
    accent: '#FBBF24',
    sources: ['voice_install'],
  },
  {
    id: 'journeys',
    label: 'Signature Journeys',
    hint: 'Wealth · Sleep · Focus · Confidence · Gym — the transformational soundtracks you built.',
    accent: '#EAB575',
    sources: ['audio_journey'],
  },
  {
    id: 'absorption',
    label: 'Absorption',
    hint: 'Chapters and study material you installed.',
    accent: '#7DD3FC',
    sources: ['absorption'],
  },
  {
    id: 'rituals',
    label: 'Rituals',
    hint: 'Pre-Performance & Team Alignment.',
    accent: '#60A5FA',
    sources: ['pre_prime', 'team_alignment'],
  },
  {
    id: 'circle',
    label: 'From Your Circle',
    hint: 'Reframes and voice gifts from the people who love you.',
    accent: '#F87171',
    sources: ['collective', 'gift_moment'],
  },
  {
    id: 'recipes',
    label: 'Session Recipes',
    hint: 'Whole immersions you can replay.',
    accent: '#34D399',
    sources: ['session_recipe', 'reclaimed_shot'],
  },
  {
    id: 'visuals',
    label: 'Visuals',
    hint: 'Images you gathered across the app.',
    accent: '#E4B15D',
    sources: ['personal_image', 'vision_board', 'aurum_snapshot', 'make_it_mine'],
  },
];

const SOURCE_LABEL = {
  pre_prime:       'Pre-Performance',
  voice_install:   'Affirmation',
  collective:      'Collective',
  gift_moment:     'Gift Moment',
  vision_board:    'Vision Board',
  aurum_snapshot:  'Aurum',
  make_it_mine:    'Personalised',
  upload:          'Uploaded',
  personal_image:  'Image',
  team_alignment:  'Team Alignment',
  reality_state:   'Reality',
  saved_state:     'State',
  alignment_save:  'Alignment',
  session_recipe:  'Recipe',
  reclaimed_shot:  'Reclaim Shot',
  audio_journey:   'Signature',
  absorption:      'Absorption',
};

// ─── Lens 2 · Life Themes ─────────────────────────────────────────
// Client-side keyword filter over title + subtitle. Any item whose
// text matches one of the keywords surfaces in the theme tile.
const LIFE_THEMES = [
  { id: 'wealth',      label: 'Wealth & Abundance',   emoji: '💰', keywords: ['wealth','abundance','billionaire','money','prosperity','rich'] },
  { id: 'sleep',       label: 'Sleep & Deep Rest',    emoji: '🌙', keywords: ['sleep','rest','night','delta','nap','dream','wind down'] },
  { id: 'focus',       label: 'Focus & Deep Work',    emoji: '🎯', keywords: ['focus','deep work','concentration','study','attention','flow'] },
  { id: 'confidence',  label: 'Confidence & Presence',emoji: '🛡️', keywords: ['confidence','presence','courage','champion','poise'] },
  { id: 'calm',        label: 'Calm & Anxiety Relief',emoji: '💗', keywords: ['calm','anxiety','stress','relief','peace','ease','soothe'] },
  { id: 'energy',      label: 'Energy & Motivation',  emoji: '⚡', keywords: ['energy','motivation','morning','activate','rise','fire','beta'] },
  { id: 'love',        label: 'Love & Connection',    emoji: '💛', keywords: ['love','connection','heart','partner','intimacy','beloved'] },
  { id: 'gratitude',   label: 'Gratitude & Reflection',emoji: '🙏', keywords: ['gratitude','thank','reflection','appreciate','grace'] },
  { id: 'health',      label: 'Health & Vitality',    emoji: '💪', keywords: ['health','vitality','healing','recovery','body','spine'] },
  { id: 'family',      label: 'Family & Parenting',   emoji: '👨‍👩‍👧', keywords: ['family','mum','dad','father','mother','grandma','grandad','child','parent','nanna'] },
  { id: 'leadership',  label: 'Leadership & Voice',   emoji: '👑', keywords: ['leader','team','voice','speaking','presentation','rockstar'] },
  { id: 'learning',    label: 'Learning & Memory',    emoji: '📖', keywords: ['learn','study','absorption','memory','chapter','knowledge'] },
  { id: 'sport',       label: 'Sport & Performance',  emoji: '🏆', keywords: ['sport','golf','football','tennis','gym','performance','run','training'] },
  { id: 'ritual_time', label: 'Morning · Evening',    emoji: '🌅', keywords: ['morning','evening','ritual','sunset','dawn','arrival'] },
];

// ─── Lens 3 · Sound Type ──────────────────────────────────────────
// Broad-brush categorisation by the audio "flavour". Uses source
// families + keyword fallback.
const SOUND_TYPES = [
  { id: 'voice',       label: 'Voice',         emoji: '🎙️', hint: 'Affirmations · Alignments · Installations',
    sources: ['voice_install','alignment_save','collective','gift_moment','pre_prime','team_alignment','reality_state','saved_state'] },
  { id: 'signature',   label: 'Music & Cinematic', emoji: '🎹', hint: 'Signature Journeys · Session Recipes',
    sources: ['audio_journey','session_recipe','reclaimed_shot'] },
  { id: 'immersion',   label: 'Immersion',     emoji: '🌊', hint: 'Absorption chapters & Vision Board.',
    sources: ['absorption','vision_board'] },
  { id: 'visuals',     label: 'Visuals',       emoji: '🖼️', hint: 'Personalised images across the app.',
    sources: ['personal_image','aurum_snapshot','make_it_mine'] },
];

const KIND_ICON = {
  audio:     Headphones,
  immersion: Sparkles,
  image:     ImageIcon,
};

const MyCollectionPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  // Which lens is expanded below the source rows. null = collapsed.
  //   'themes' = Life Themes tiles (14)
  //   'sounds' = Sound Type tiles (4)
  const [lens, setLens] = useState(null);
  const [themeFilter, setThemeFilter] = useState(null);   // theme id | null
  const [soundFilter, setSoundFilter] = useState(null);   // sound id | null
  const [playingId, setPlayingId] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const audioRef = useRef(null);
  const pendingDeleteRef = useRef({}); // id+source → timeoutId
  const { showUndo, toast } = useUndoToast();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/library/items`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (!cancelled) setItems(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Hard audio teardown on unmount + page-hide.
  useEffect(() => {
    const tear = () => { const a = audioRef.current; if (a) { a.pause(); a.src = ''; } };
    window.addEventListener('pagehide', tear);
    return () => { window.removeEventListener('pagehide', tear); tear(); };
  }, []);

  const filtered = useMemo(() => {
    if (tab === 'all') return items;
    return items.filter((i) => i.kind === tab);
  }, [items, tab]);

  // Item text used for keyword matching in life themes.
  const itemText = (it) => `${it.title || ''} ${it.subtitle || ''}`.toLowerCase();

  const themeMatches = (it, theme) =>
    theme.keywords.some((kw) => itemText(it).includes(kw));

  const soundMatches = (it, sound) =>
    (sound.sources || []).includes(it.source);

  // Group all items into their CATEGORIES for the category-first view
  // shown when the "All" tab is active. Empty categories collapse.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const cat of CATEGORIES) {
      const bucket = items.filter((i) => cat.sources.includes(i.source));
      if (bucket.length > 0) map.set(cat.id, { cat, items: bucket });
    }
    return Array.from(map.values());
  }, [items]);

  // Count items per theme / sound so tiles can show live totals and
  // empty tiles collapse (matches how Calm hides empty shelves).
  const themeCounts = useMemo(() => {
    const c = {};
    for (const theme of LIFE_THEMES) {
      c[theme.id] = items.filter((it) => themeMatches(it, theme)).length;
    }
    return c;
  }, [items]);

  const soundCounts = useMemo(() => {
    const c = {};
    for (const s of SOUND_TYPES) {
      c[s.id] = items.filter((it) => soundMatches(it, s)).length;
    }
    return c;
  }, [items]);

  // Items shown inside an expanded theme / sound tile.
  const themeItems = useMemo(() => {
    if (!themeFilter) return [];
    const theme = LIFE_THEMES.find((t) => t.id === themeFilter);
    if (!theme) return [];
    return items.filter((it) => themeMatches(it, theme));
  }, [items, themeFilter]);

  const soundItems = useMemo(() => {
    if (!soundFilter) return [];
    const sound = SOUND_TYPES.find((s) => s.id === soundFilter);
    if (!sound) return [];
    return items.filter((it) => soundMatches(it, sound));
  }, [items, soundFilter]);

  const totalAudios = useMemo(
    () => items.filter((i) => i.kind === 'audio').length,
    [items],
  );

  const togglePlay = (item) => {
    if (!item.audio_url) return;
    const src = item.audio_url.startsWith('http') ? item.audio_url : `${API}${item.audio_url}`;
    const a = audioRef.current;
    if (!a) return;
    if (playingId === item.id) {
      a.pause();
      setPlayingId(null);
    } else {
      a.src = src;
      a.currentTime = 0;
      a.volume = 0.85;
      a.play().then(() => setPlayingId(item.id)).catch(() => setPlayingId(null));
    }
  };

  const deleteItem = async (item) => {
    // Soft delete with 4 s undo window. We remove the item from the
    // visible list immediately, then schedule the actual backend DELETE
    // 4 seconds later — clearable by tapping Undo on the toast.
    const key = `${item.source}:${item.id}`;
    const removedItem = item;
    const prevIndex = items.findIndex((i) => i.id === item.id && i.source === item.source);
    setItems((arr) => arr.filter((i) => !(i.id === item.id && i.source === item.source)));
    if (playingId === item.id) {
      try { audioRef.current?.pause(); } catch (_) { /* noop */ }
      setPlayingId(null);
    }
    const fireDelete = () => {
      delete pendingDeleteRef.current[key];
      fetch(`${API}/api/library/items/${item.source}/${encodeURIComponent(item.id)}`, {
        method: 'DELETE',
        headers: auth(),
      }).catch(() => {
        // On failure: best-effort restore.
        setItems((arr) => {
          if (arr.find((i) => i.id === removedItem.id && i.source === removedItem.source)) return arr;
          const copy = arr.slice();
          copy.splice(Math.min(prevIndex < 0 ? 0 : prevIndex, copy.length), 0, removedItem);
          return copy;
        });
      });
    };
    const onUndo = () => {
      const t = pendingDeleteRef.current[key];
      if (t) { clearTimeout(t); delete pendingDeleteRef.current[key]; }
      setItems((arr) => {
        if (arr.find((i) => i.id === removedItem.id && i.source === removedItem.source)) return arr;
        const copy = arr.slice();
        copy.splice(Math.min(prevIndex < 0 ? 0 : prevIndex, copy.length), 0, removedItem);
        return copy;
      });
    };
    pendingDeleteRef.current[key] = setTimeout(fireDelete, 4000);
    showUndo('Removed', onUndo);
  };

  // Clean up pending deletes on unmount — fire-and-forget anything still
  // queued so server state matches the user's intent if they navigate away.
  useEffect(() => () => {
    Object.values(pendingDeleteRef.current).forEach((t) => clearTimeout(t));
  }, []);

  return (
    <div
      className="min-h-screen pb-24"
      style={premiumPageBg()}
      data-testid="my-collection-page"
    >
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} />

      {/* Header */}
      <header className="px-6 pt-6 pb-2 flex items-center max-w-md mx-auto">
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
          className="p-2 -ml-2 rounded-lg hover:bg-white/10 transition-colors"
          data-testid="collection-back-btn"
          aria-label="Back"
        >
          <ArrowLeft size={20} className="text-white/55" />
        </button>
        <p style={premiumEyebrow} className="ml-2">My Collection</p>
      </header>

      <div className="px-6 max-w-md mx-auto">
        <h1
          className="text-3xl text-white mt-3"
          style={{ ...premiumHeading, fontSize: 'clamp(2rem, 5.5vw, 2.6rem)' }}
          data-testid="collection-title"
        >
          Your personal audio library.
        </h1>
        <div style={{ ...premiumDivider(), marginTop: '14px', width: '64px' }} aria-hidden />
        <p className="mt-4 text-sm text-white/55 leading-relaxed">
          Everything you&apos;ve curated across the app — Realities, Alignments,
          States, Installations, Rituals — in one calm library. Tap any
          card to play.
        </p>

        {!loading && totalAudios > 0 && (
          <p
            className="mt-3 text-[11px] tracking-[0.28em] uppercase text-amber-300/70"
            data-testid="collection-total-audios"
          >
            {totalAudios} {totalAudios === 1 ? 'audio' : 'audios'} in your library
          </p>
        )}

        {/* Tabs */}
        <div className="mt-7 flex gap-1.5 overflow-x-auto pb-1" data-testid="collection-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] tracking-[0.18em] uppercase transition-all whitespace-nowrap ${
                  active ? 'text-[#1f3a5c]' : 'text-white/55 hover:text-white/85'
                }`}
                style={active ? {
                  background: 'linear-gradient(180deg, #f1d29c 0%, #d9a55a 100%)',
                  boxShadow: '0 6px 20px -10px rgba(217,165,90,0.5)',
                } : {
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
                data-testid={`tab-${t.id}`}
              >
                <Icon size={12} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Body — category rows on All, flat list on kind tabs */}
        {loading && (
          <p className="mt-8 text-center text-white/40 text-sm py-12">Loading your collection…</p>
        )}

        {!loading && items.length === 0 && (
          <div
            className="mt-6 rounded-2xl p-10 text-center"
            style={premiumCard({ accent: ACCENTS.library })}
          >
            <Sparkles size={28} className="mx-auto mb-3 text-amber-300/55" />
            <p className="text-white/65 text-sm">
              Nothing here yet. Anything you create across the app lands here automatically.
            </p>
          </div>
        )}

        {!loading && items.length > 0 && tab === 'all' && (
          <div className="mt-6 space-y-8" data-testid="collection-categories">
            {grouped.map(({ cat, items: bucket }) => (
              <CategoryRow
                key={cat.id}
                category={cat}
                items={bucket}
                playingId={playingId}
                onTogglePlay={togglePlay}
                onShare={setShareTarget}
                onDelete={deleteItem}
              />
            ))}

            {/* ─── Lens 2 · Life Themes ─────────────────────────── */}
            <LensToggle
              id="themes"
              label="Explore by Theme"
              hint="14 shelves — the way Calm and Headspace let you browse by why you're reaching for it."
              open={lens === 'themes'}
              onToggle={() => setLens(lens === 'themes' ? null : 'themes')}
            />
            {lens === 'themes' && (
              <div>
                <div className="grid grid-cols-2 gap-2.5" data-testid="theme-tiles">
                  {LIFE_THEMES.map((theme) => {
                    const count = themeCounts[theme.id] || 0;
                    const active = themeFilter === theme.id;
                    return (
                      <button
                        key={theme.id}
                        onClick={() => setThemeFilter(active ? null : theme.id)}
                        disabled={count === 0}
                        className="text-left rounded-2xl p-4 transition-all disabled:opacity-30"
                        style={{
                          background: active
                            ? 'linear-gradient(135deg, rgba(241,210,156,0.14), rgba(241,210,156,0.04))'
                            : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${active ? 'rgba(241,210,156,0.55)' : 'rgba(255,255,255,0.08)'}`,
                          boxShadow: active ? '0 8px 22px -12px rgba(217,165,90,0.5)' : 'none',
                        }}
                        data-testid={`theme-${theme.id}`}
                      >
                        <div className="text-2xl">{theme.emoji}</div>
                        <div className="mt-2 text-[13px] text-white/90 leading-tight">{theme.label}</div>
                        <div className="mt-1.5 text-[10px] tracking-[0.24em] uppercase text-amber-300/60">
                          {count} {count === 1 ? 'audio' : 'audios'}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {themeFilter && themeItems.length > 0 && (
                  <div className="mt-5 space-y-2.5" data-testid="theme-items">
                    {themeItems.map((item, idx) => (
                      <ItemCard
                        key={`t-${item.source}-${item.id}-${idx}`}
                        item={item}
                        isPlaying={playingId === item.id}
                        onTogglePlay={() => togglePlay(item)}
                        onShare={() => setShareTarget(item)}
                        onDelete={() => deleteItem(item)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ─── Lens 3 · Sound Type ──────────────────────────── */}
            <LensToggle
              id="sounds"
              label="Explore by Sound"
              hint="Voice · Music · Immersion · Visuals — pick the flavour your body's asking for."
              open={lens === 'sounds'}
              onToggle={() => setLens(lens === 'sounds' ? null : 'sounds')}
            />
            {lens === 'sounds' && (
              <div>
                <div className="grid grid-cols-2 gap-2.5" data-testid="sound-tiles">
                  {SOUND_TYPES.map((sound) => {
                    const count = soundCounts[sound.id] || 0;
                    const active = soundFilter === sound.id;
                    return (
                      <button
                        key={sound.id}
                        onClick={() => setSoundFilter(active ? null : sound.id)}
                        disabled={count === 0}
                        className="text-left rounded-2xl p-4 transition-all disabled:opacity-30"
                        style={{
                          background: active
                            ? 'linear-gradient(135deg, rgba(122,180,240,0.16), rgba(122,180,240,0.04))'
                            : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${active ? 'rgba(122,180,240,0.55)' : 'rgba(255,255,255,0.08)'}`,
                          boxShadow: active ? '0 8px 22px -12px rgba(96,165,250,0.5)' : 'none',
                        }}
                        data-testid={`sound-${sound.id}`}
                      >
                        <div className="text-2xl">{sound.emoji}</div>
                        <div className="mt-2 text-[13px] text-white/90 leading-tight">{sound.label}</div>
                        <div className="mt-1 text-[11px] text-white/45 leading-tight">{sound.hint}</div>
                        <div className="mt-1.5 text-[10px] tracking-[0.24em] uppercase text-sky-300/60">
                          {count} {count === 1 ? 'audio' : 'audios'}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {soundFilter && soundItems.length > 0 && (
                  <div className="mt-5 space-y-2.5" data-testid="sound-items">
                    {soundItems.map((item, idx) => (
                      <ItemCard
                        key={`s-${item.source}-${item.id}-${idx}`}
                        item={item}
                        isPlaying={playingId === item.id}
                        onTogglePlay={() => togglePlay(item)}
                        onShare={() => setShareTarget(item)}
                        onDelete={() => deleteItem(item)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!loading && items.length > 0 && tab !== 'all' && (
          <div className="mt-6 space-y-3" data-testid="collection-list">
            {filtered.length === 0 ? (
              <div
                className="rounded-2xl p-10 text-center"
                style={premiumCard({ accent: ACCENTS.library })}
              >
                <Sparkles size={28} className="mx-auto mb-3 text-amber-300/55" />
                <p className="text-white/65 text-sm">
                  No {tab === 'audio' ? 'audios' : tab === 'immersion' ? 'immersions' : 'images'} yet.
                </p>
              </div>
            ) : (
              filtered.map((item, idx) => (
                <ItemCard
                  key={`${item.source}-${item.id}-${idx}`}
                  item={item}
                  isPlaying={playingId === item.id}
                  onTogglePlay={() => togglePlay(item)}
                  onShare={() => setShareTarget(item)}
                  onDelete={() => deleteItem(item)}
                />
              ))
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {shareTarget && (
          <ShareCardModal
            item={shareTarget}
            onClose={() => setShareTarget(null)}
            onShared={(updated) => {
              setItems((arr) => arr.map((i) => (i.id === updated.id && i.source === updated.source ? { ...i, ...updated } : i)));
            }}
          />
        )}
      </AnimatePresence>
      {toast}
    </div>
  );
};

// ─── Lens toggle divider (Explore by Theme / Sound) ──────────────
const LensToggle = ({ id, label, hint, open, onToggle }) => (
  <button
    onClick={onToggle}
    className="w-full text-left rounded-2xl px-4 py-3.5 transition-all"
    style={{
      background: open
        ? 'linear-gradient(135deg, rgba(241,210,156,0.10), rgba(241,210,156,0.02))'
        : 'rgba(255,255,255,0.03)',
      border: `1px solid ${open ? 'rgba(241,210,156,0.45)' : 'rgba(255,255,255,0.08)'}`,
    }}
    data-testid={`lens-toggle-${id}`}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] tracking-[0.28em] uppercase text-amber-300/75">
          {label}
        </p>
        <p className="mt-1 text-[12px] text-white/50 leading-snug">{hint}</p>
      </div>
      <div
        className="flex-shrink-0 text-[10px] tracking-[0.24em] uppercase text-white/60"
      >
        {open ? 'Collapse' : 'Open'}
      </div>
    </div>
  </button>
);

// ─── One category row (All view) ─────────────────────────────────
// Beautiful category strip: eyebrow + hint + horizontal scroll of
// items in that category. Ordering matches CATEGORIES definition.
const CategoryRow = ({ category, items: rowItems, playingId, onTogglePlay, onShare, onDelete }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? rowItems : rowItems.slice(0, 6);
  const hasMore = rowItems.length > 6;
  return (
    <section
      className="relative"
      data-testid={`category-${category.id}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              aria-hidden
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: category.accent, boxShadow: `0 0 10px ${category.accent}88` }}
            />
            <h3 className="text-white/95 text-lg font-light truncate">{category.label}</h3>
            <span className="text-[10px] tracking-[0.28em] uppercase text-white/40">
              {rowItems.length}
            </span>
          </div>
          <p className="mt-1 text-[12px] text-white/45 leading-snug">{category.hint}</p>
        </div>
        {hasMore && (
          <button
            onClick={() => setShowAll((v) => !v)}
            className="flex-shrink-0 text-[10px] tracking-[0.24em] uppercase text-amber-300/70 hover:text-amber-200 transition-colors"
            data-testid={`category-${category.id}-see-all`}
          >
            {showAll ? 'Collapse' : `See all ${rowItems.length}`}
          </button>
        )}
      </div>
      <div className="mt-3 space-y-2.5">
        {visible.map((item, idx) => (
          <ItemCard
            key={`${item.source}-${item.id}-${idx}`}
            item={item}
            isPlaying={playingId === item.id}
            onTogglePlay={() => onTogglePlay(item)}
            onShare={() => onShare(item)}
            onDelete={() => onDelete(item)}
          />
        ))}
      </div>
    </section>
  );
};

// ─── One item row ─────────────────────────────────────────────────
const ItemCard = ({ item, isPlaying, onTogglePlay, onShare, onDelete }) => {
  const Icon = KIND_ICON[item.kind] || Sparkles;
  const label = SOURCE_LABEL[item.source] || item.source;
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Auto-revert the confirm state after 4s if the user doesn't act.
  useEffect(() => {
    if (!confirmingDelete) return undefined;
    const t = setTimeout(() => setConfirmingDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmingDelete]);
  const handleDelete = () => {
    if (!confirmingDelete) { setConfirmingDelete(true); return; }
    setConfirmingDelete(false);
    onDelete();
  };
  const dateStr = (() => {
    if (!item.created_at) return '';
    try { return new Date(item.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }); }
    catch { return ''; }
  })();
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-4"
      style={premiumCard({ accent: ACCENTS.library, goldKiss: true })}
      data-testid={`item-${item.kind}-${item.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Visual */}
        <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center"
          style={{ background: 'rgba(241,210,156,0.10)', border: '1px solid rgba(241,210,156,0.18)' }}>
          {item.image_url ? (
            <img src={item.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <Icon size={22} className="text-amber-200/85" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white/90 text-sm font-medium leading-snug truncate">{item.title}</p>
          <p className="text-white/40 text-[11px] tracking-[0.18em] uppercase mt-0.5">
            {label}{dateStr ? ` · ${dateStr}` : ''}
          </p>
          {item.shared && (item.opens ?? 0) > 0 && (
            <p
              className="mt-1 inline-flex items-center gap-1 text-amber-200/85 text-[10px] tracking-[0.18em] uppercase"
              data-testid={`item-opens-${item.id}`}
              title={`${item.opens} ${item.opens === 1 ? 'person has' : 'people have'} opened your card`}
            >
              <Eye size={10} />
              {item.opens} opened
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {item.audio_url && (
            <button
              onClick={onTogglePlay}
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: isPlaying ? 'rgba(241,210,156,0.25)' : 'rgba(255,255,255,0.05)', border: '1px solid rgba(241,210,156,0.25)' }}
              data-testid={`item-play-${item.id}`}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={14} className="text-amber-200" /> : <Play size={14} className="text-white/85 ml-0.5" />}
            </button>
          )}
          <button
            onClick={onShare}
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{
              background: item.shared ? 'rgba(241,210,156,0.18)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${item.shared ? 'rgba(241,210,156,0.5)' : 'rgba(241,210,156,0.18)'}`,
            }}
            data-testid={`item-share-${item.id}`}
            aria-label="Share"
          >
            {item.shared ? <Check size={14} className="text-amber-200" /> : <Share2 size={14} className="text-white/85" />}
          </button>
          <button
            onClick={handleDelete}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all"
            style={{
              background: confirmingDelete ? 'rgba(220, 80, 70, 0.28)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${confirmingDelete ? 'rgba(220, 80, 70, 0.7)' : 'rgba(241,210,156,0.18)'}`,
            }}
            data-testid={`item-delete-${item.id}`}
            aria-label={confirmingDelete ? 'Tap again to confirm' : 'Delete'}
            title={confirmingDelete ? 'Tap again to confirm' : 'Delete'}
          >
            <Trash2 size={14} className={confirmingDelete ? 'text-red-300' : 'text-white/55'} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Share modal — generates a "Session Recipe" card via html2canvas ──
const ShareCardModal = ({ item, onClose, onShared }) => {
  const cardRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [shareId, setShareId] = useState(item.share_id || null);
  const [shared, setShared] = useState(!!item.shared);

  const goShare = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/library/share/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify({ item_id: item.id, source: item.source, kind: item.kind, title: item.title }),
      });
      const data = await res.json();
      setShared(data.shared);
      setShareId(data.share_id);
      onShared({ id: item.id, source: item.source, shared: data.shared, share_id: data.share_id });
    } finally {
      setSaving(false);
    }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setSaving(true);
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: '#03060f', scale: 2, useCORS: true });
      const link = document.createElement('a');
      link.download = `reclaim-${(item.title || 'moment').slice(0, 24).replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch {
      // noop
    } finally {
      setSaving(false);
    }
  };

  const shareLink = shareId ? `${window.location.origin}/s/${shareId}` : '';
  const copyLink = () => {
    if (!shareLink) return;
    navigator.clipboard?.writeText(shareLink);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center px-6"
      style={{ background: 'rgba(3,6,15,0.92)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid="share-modal"
    >
      {/* Session Recipe card */}
      <motion.div
        ref={cardRef}
        initial={{ scale: 0.95 }} animate={{ scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="rounded-3xl p-8 max-w-sm w-full"
        style={{
          background: 'linear-gradient(180deg, #1f3a5c 0%, #0c1a30 60%, #03060f 100%)',
          border: '1px solid rgba(241,210,156,0.32)',
          boxShadow: '0 24px 80px -20px rgba(0,0,0,0.85), 0 0 80px rgba(241,210,156,0.08)',
        }}
      >
        <p style={{ ...premiumEyebrow, textAlign: 'center' }}>{SOURCE_LABEL[item.source] || 'Reclaim'}</p>
        {item.image_url && (
          <img
            src={item.image_url}
            alt=""
            className="mt-6 w-full aspect-square object-cover rounded-2xl"
            style={{ border: '1px solid rgba(241,210,156,0.18)' }}
            crossOrigin="anonymous"
          />
        )}
        <h2
          className="mt-6 text-center text-2xl text-white leading-snug"
          style={premiumHeading}
        >
          &ldquo;{item.title}&rdquo;
        </h2>
        <div style={{ ...premiumDivider(), marginTop: '20px', width: '60%', marginLeft: 'auto', marginRight: 'auto' }} aria-hidden />
        <p className="mt-5 text-center text-[10px] tracking-[0.4em] uppercase text-amber-200/75">
          Reclaim
        </p>
      </motion.div>

      {/* Actions */}
      <div className="mt-6 w-full max-w-sm space-y-2" onClick={(e) => e.stopPropagation()}>
        {!shared && (
          <button
            onClick={goShare}
            disabled={saving}
            className="w-full py-3.5 rounded-2xl font-medium"
            style={{
              background: 'linear-gradient(180deg, #f1d29c 0%, #d9a55a 100%)',
              color: '#1f3a5c',
              boxShadow: '0 12px 30px -16px rgba(217,165,90,0.7)',
            }}
            data-testid="share-public-btn"
          >
            {saving ? 'Preparing…' : 'Make shareable'}
          </button>
        )}
        {shared && shareId && (
          <div
            className="w-full py-3.5 px-4 rounded-2xl flex items-center justify-between"
            style={premiumCard({ accent: ACCENTS.library, goldKiss: true, density: 'high' })}
          >
            <span className="text-[11px] tracking-[0.18em] uppercase text-amber-200">Public link</span>
            <button
              onClick={copyLink}
              className="text-xs text-white/85 underline-offset-4 hover:underline"
              data-testid="share-copy-link"
            >
              Copy
            </button>
          </div>
        )}
        <button
          onClick={downloadCard}
          disabled={saving}
          className="w-full py-3.5 rounded-2xl flex items-center justify-center gap-2"
          style={premiumCard({ accent: ACCENTS.library, density: 'low' })}
          data-testid="share-download-card"
        >
          <Download size={16} className="text-amber-200/85" />
          <span className="text-sm text-white/85">Save card to camera roll</span>
        </button>
        <button
          onClick={onClose}
          className="w-full py-2.5 text-[11px] tracking-[0.18em] uppercase text-white/55 hover:text-white/85"
          data-testid="share-close"
        >
          Done
        </button>
      </div>
    </motion.div>
  );
};

export default MyCollectionPage;
